import type {
  WorkshopDto,
  CreateWorkshopInput,
  UpdateWorkshopInput,
  RegistrationConfirmationDto,
  RegistrationDto,
  MyRegistrationDto,
  CreateFeedbackReportInput,
  FeedbackReportDto,
  RegisterForWorkshopInput,
} from '@workshop-hst/shared';

const ADMIN_KEY_STORAGE = 'workshop-hst:admin-key';

export function getAdminKey(): string | null {
  return localStorage.getItem(ADMIN_KEY_STORAGE);
}

export function setAdminKey(key: string): void {
  localStorage.setItem(ADMIN_KEY_STORAGE, key);
  window.dispatchEvent(new CustomEvent('admin-key-changed'));
}

export function clearAdminKey(): void {
  localStorage.removeItem(ADMIN_KEY_STORAGE);
  window.dispatchEvent(new CustomEvent('admin-key-changed'));
}

function getErrorMessage(body: unknown, status: number): string {
  return body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
    ? body.error
    : `HTTP ${status}`;
}

async function readResponseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return JSON.parse(text);
  }

  const title = text.match(/<title>(.*?)<\/title>/is)?.[1]?.replace(/\s+/g, ' ').trim();
  const snippet = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
  const detail = title || snippet || contentType || 'non-JSON response';
  throw new ApiError(`HTTP ${res.status}: ${detail}`, res.status);
}

async function request<T>(
  path: string,
  init: RequestInit & { admin?: boolean; subscriber?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (init.admin) {
    const key = getAdminKey();
    if (key) headers.set('X-Admin-Key', key);
  }
  // init.subscriber is preserved on the signature for callers that mark a
  // request as "needs subscriber auth." Session cookies travel automatically,
  // so no header needs to be added here.

  const res = await fetch(path, { ...init, headers });
  const body = await readResponseBody(res);
  if (!res.ok) {
    throw new ApiError(getErrorMessage(body, res.status), res.status);
  }
  return body as T;
}

async function uploadAdminFile<T>(path: string, file: File): Promise<T> {
  const key = getAdminKey();
  const form = new FormData();
  form.append('file', file);
  const headers = new Headers({ Accept: 'application/json' });
  if (key) headers.set('X-Admin-Key', key);

  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: form,
  });
  const body = await readResponseBody(res);
  if (!res.ok) {
    throw new ApiError(getErrorMessage(body, res.status), res.status);
  }
  return body as T;
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

export type WorkshopsResponse = { workshops: WorkshopDto[] };
export type WorkshopResponse = { workshop: WorkshopDto };
export type RecordingUrlResponse = { url: string; expiresAt: string | null };
export type CourseDiscount = {
  code: string;
  tier: 'free' | 'subscriber' | 'insider';
  productName: string;
  productListPriceCents: number;
  listPriceCents: number;
  discountAmountCents: number;
  currency: string;
  checkoutUrl: string;
};
export type CourseDiscountResponse = { claim: CourseDiscount };

export const api = {
  listWorkshops: (status: 'upcoming' | 'past' | 'all') =>
    request<WorkshopsResponse>(`/api/workshops?status=${status}`),
  getWorkshop: (slug: string) => request<WorkshopResponse>(`/api/workshops/${slug}`),
  recordingUrl: (slug: string) =>
    request<RecordingUrlResponse>(`/api/workshops/${slug}/recording-url`, {
      subscriber: true,
    }),
  register: (slug: string, input: RegisterForWorkshopInput) =>
    request<RegistrationConfirmationDto>(`/api/workshops/${slug}/register`, {
      method: 'POST',
      subscriber: true,
      body: JSON.stringify(input),
    }),
  /**
   * Look up whether the signed-in subscriber has already registered for this
   * workshop. Returns null when they haven't. Throws ApiError(401) when no
   * identity is present — caller should gate the query on `identity != null`.
   */
  myRegistration: (slug: string) =>
    request<MyRegistrationDto>(`/api/workshops/${slug}/my-registration`, {
      subscriber: true,
    }),
  unregister: (slug: string) =>
    request<{ ok: boolean; notificationSent: boolean }>(`/api/workshops/${slug}/register`, {
      method: 'DELETE',
      subscriber: true,
    }),
  submitFeedback: (input: CreateFeedbackReportInput) =>
    request<{ report: FeedbackReportDto }>('/api/feedback', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  getCourseDiscount: () =>
    request<CourseDiscountResponse>('/api/course-discount/claim', {
      subscriber: true,
    }),
  getPublicCourseDiscount: () =>
    request<CourseDiscountResponse>('/api/course-discount/public'),
  claimCourseDiscount: () =>
    request<CourseDiscountResponse>('/api/course-discount/claim', {
      method: 'POST',
      subscriber: true,
    }),

  admin: {
    listWorkshops: () => request<WorkshopsResponse>('/api/admin/workshops', { admin: true }),
    listFeedbackReports: () =>
      request<{ reports: FeedbackReportDto[] }>('/api/admin/feedback', { admin: true }),
    updateFeedbackReportStatus: (id: string, status: 'open' | 'closed') =>
      request<{ report: FeedbackReportDto }>(`/api/admin/feedback/${id}`, {
        method: 'PATCH',
        admin: true,
        body: JSON.stringify({ status }),
      }),
    getWorkshop: (id: string) =>
      request<WorkshopResponse>(`/api/admin/workshops/${id}`, { admin: true }),
    getWorkshopBySlug: (slug: string) =>
      request<WorkshopResponse>(`/api/admin/workshops/by-slug/${slug}`, { admin: true }),
    listRegistrations: (id: string) =>
      request<{ registrations: RegistrationDto[] }>(
        `/api/admin/workshops/${id}/registrations`,
        { admin: true },
      ),
    /**
     * Download registrations as CSV. The endpoint requires the admin header, so we
     * fetch as a Blob and trigger a download via a temporary anchor (rather than just
     * setting window.location).
     */
    downloadRegistrationsCsv: async (workshopId: string, filename: string): Promise<void> => {
      const key = getAdminKey();
      const res = await fetch(`/api/admin/workshops/${workshopId}/registrations.csv`, {
        headers: key ? { 'X-Admin-Key': key } : undefined,
      });
      if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    createWorkshop: (input: CreateWorkshopInput) =>
      request<WorkshopResponse>('/api/admin/workshops', {
        method: 'POST',
        admin: true,
        body: JSON.stringify(input),
      }),
    updateWorkshop: (id: string, input: UpdateWorkshopInput) =>
      request<WorkshopResponse>(`/api/admin/workshops/${id}`, {
        method: 'PATCH',
        admin: true,
        body: JSON.stringify(input),
      }),
    deleteWorkshop: (id: string) =>
      request<{ ok: boolean }>(`/api/admin/workshops/${id}`, {
        method: 'DELETE',
        admin: true,
      }),
    removePastWorkshop: (id: string) =>
      request<{ ok: boolean }>(`/api/admin/workshops/${id}/remove`, {
        method: 'DELETE',
        admin: true,
      }),
    regenerateImage: (id: string) =>
      request<WorkshopResponse>(`/api/admin/workshops/${id}/regenerate-image`, {
        method: 'POST',
        admin: true,
      }),
    generateCoverCandidates: (id: string, n = 3) =>
      request<{ candidates: Array<{ url: string; prompt: string }>; errors: string[] }>(
        `/api/admin/workshops/${id}/cover-candidates?n=${n}`,
        { method: 'POST', admin: true },
      ),
    selectCoverCandidate: (id: string, url: string, prompt: string | null) =>
      request<WorkshopResponse>(`/api/admin/workshops/${id}/select-cover`, {
        method: 'POST',
        admin: true,
        body: JSON.stringify({ url, prompt }),
      }),
    regenerateZoomMeeting: (id: string) =>
      request<WorkshopResponse>(`/api/admin/workshops/${id}/regenerate-zoom`, {
        method: 'POST',
        admin: true,
      }),
    adoptZoomMeeting: (id: string, joinUrl: string) =>
      request<WorkshopResponse>(`/api/admin/workshops/${id}/adopt-zoom`, {
        method: 'POST',
        admin: true,
        body: JSON.stringify({ joinUrl }),
      }),
    publishWorkshop: (id: string) =>
      request<WorkshopResponse>(`/api/admin/workshops/${id}/publish`, {
        method: 'POST',
        admin: true,
      }),
    unpublishWorkshop: (id: string) =>
      request<WorkshopResponse>(`/api/admin/workshops/${id}/unpublish`, {
        method: 'POST',
        admin: true,
      }),
    suggestCompanies: (q: string) =>
      request<{ suggestions: Array<{ name: string; domain: string; logoUrl: string | null }> }>(
        `/api/admin/companies/suggest?q=${encodeURIComponent(q)}`,
        { admin: true },
      ),
    ogPreview: (path: string) =>
      request<{
        url: string;
        title: string | null;
        description: string | null;
        ogTitle: string | null;
        ogDescription: string | null;
        ogImage: string | null;
        ogUrl: string | null;
        ogType: string | null;
        ogSiteName: string | null;
        twitterCard: string | null;
        twitterTitle: string | null;
        twitterDescription: string | null;
        twitterImage: string | null;
      }>(`/api/admin/og-preview?path=${encodeURIComponent(path)}`, { admin: true }),
    uploadGuestHeadshot: (file: File) =>
      uploadAdminFile<{ url: string }>('/api/admin/uploads/guest-headshot', file),
    uploadHostHeadshot: (file: File) =>
      uploadAdminFile<{ url: string }>('/api/admin/uploads/host-headshot', file),
    uploadCompanyLogo: (file: File) =>
      uploadAdminFile<{ url: string }>('/api/admin/uploads/company-logo', file),
    /**
     * Override the AI-generated cover image with a manually-uploaded one.
     * Multipart, so it can't use the JSON `request` helper directly.
     */
    uploadCoverImage: (id: string, file: File) =>
      uploadAdminFile<WorkshopResponse>(`/api/admin/workshops/${id}/cover-image`, file),
    attachRecording: (id: string, recordingPlayUrl: string) =>
      request<WorkshopResponse>(`/api/admin/workshops/${id}/recording`, {
        method: 'POST',
        admin: true,
        body: JSON.stringify({ recordingPlayUrl }),
      }),
    recordingUrl: (id: string) =>
      request<RecordingUrlResponse>(`/api/admin/workshops/${id}/recording-url`, {
        admin: true,
      }),
  },
};
